import { c as _c } from "react/compiler-runtime";
import { Avatar, AvatarFallback, AvatarImage } from '@achra/ui/avatar';
import { Button } from '@achra/ui/button';
import { Card, CardContent, CardHeader } from '@achra/ui/card';
import { ArrowRight, Info } from 'lucide-react';
import Link from 'next/link';
import { BuilderStatus, type ResourceOperatorFieldsFragment } from '@/modules/__generated__/graphql/switchboard-generated';
import { formatMonthYear } from '@/modules/shared/lib/date';
import { OperatorKeyPoint } from './operator-key-point';
import type { Route } from 'next';
export interface Operator {
  id: string;
  name: string;
  description: string;
  activeSince: string;
  setupTime: string;
  recurringCost: string;
}
interface ConfigureWithCallback {
  onSelectOperator: (operatorId: string) => void;
  configureServicesHref?: never;
}
interface ConfigureWithHref {
  onSelectOperator?: never;
  configureServicesHref: Route;
}
interface BaseOperatorCardProps {
  operator: ResourceOperatorFieldsFragment;
  showMoreInfo?: boolean;
  moreInfoHref?: Route;
}
export type OperatorCardProps = BaseOperatorCardProps & (ConfigureWithCallback | ConfigureWithHref);
const OPERATOR_STATUS_LABELS_MAP: Record<BuilderStatus, string> = {
  [BuilderStatus.Active]: 'Active',
  [BuilderStatus.Inactive]: 'Inactive',
  [BuilderStatus.OnHold]: 'On Hold',
  [BuilderStatus.Completed]: 'Completed',
  [BuilderStatus.Archived]: 'Archived'
};
function OperatorCard(t0) {
  const $ = _c(51);
  const {
    operator,
    onSelectOperator,
    configureServicesHref,
    showMoreInfo: t1,
    moreInfoHref
  } = t0;
  const showMoreInfo = t1 === undefined ? false : t1;
  const resolvedMoreInfoHref = moreInfoHref ?? `/services/operators/${operator.slug}` as Route;
  const t2 = operator.name ?? "";
  let t3;
  if ($[0] !== operator.icon || $[1] !== t2) {
    t3 = <AvatarImage src={operator.icon} alt={t2} />;
    $[0] = operator.icon;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== operator.name) {
    t4 = operator.name?.charAt(0).toUpperCase() ?? "U";
    $[3] = operator.name;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t4) {
    t5 = <AvatarFallback>{t4}</AvatarFallback>;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t3 || $[8] !== t5) {
    t6 = <Avatar className="size-12 sm:size-10">{t3}{t5}</Avatar>;
    $[7] = t3;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== operator.code) {
    t7 = <span className="text-foreground/30">{operator.code}</span>;
    $[10] = operator.code;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== operator.name) {
    t8 = <span className="text-foreground">{operator.name}</span>;
    $[12] = operator.name;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t7 || $[15] !== t8) {
    t9 = <div className="flex gap-1 text-base/6 font-semibold">{t7}{t8}</div>;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t6 || $[18] !== t9) {
    t10 = <CardHeader className="gap-0 p-0"><div className="flex items-center gap-2">{t6}{t9}</div></CardHeader>;
    $[17] = t6;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== operator.description) {
    t11 = <span className="text-foreground text-sm/5.5">{operator.description}</span>;
    $[20] = operator.description;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== operator.lastModified) {
    t12 = formatMonthYear(operator.lastModified);
    $[22] = operator.lastModified;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t12) {
    t13 = <OperatorKeyPoint label="Last Active" value={t12} />;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const t14 = OPERATOR_STATUS_LABELS_MAP[operator.status ?? BuilderStatus.Active];
  let t15;
  if ($[26] !== t14) {
    t15 = <OperatorKeyPoint label="Status" value={t14} />;
    $[26] = t14;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== t13 || $[29] !== t15) {
    t16 = <div className="grid grid-cols-2 gap-2">{t13}{t15}</div>;
    $[28] = t13;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  const t17 = `${operator.contributors.length} contributors`;
  let t18;
  if ($[31] !== t17) {
    t18 = <OperatorKeyPoint label="Team Size" value={t17} />;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t16 || $[34] !== t18) {
    t19 = <div className="flex flex-col gap-2">{t16}{t18}</div>;
    $[33] = t16;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== t11 || $[37] !== t19) {
    t20 = <CardContent className="flex flex-col gap-4 p-0">{t11}{t19}</CardContent>;
    $[36] = t11;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== configureServicesHref || $[40] !== onSelectOperator || $[41] !== operator.id) {
    t21 = onSelectOperator ? <Button variant="default" className="w-full" onClick={() => {
      onSelectOperator(operator.id);
    }}>Configure Services<ArrowRight className="size-4" /></Button> : <Button variant="default" className="w-full" asChild={true}><Link href={configureServicesHref}>Configure Services<ArrowRight className="size-4" /></Link></Button>;
    $[39] = configureServicesHref;
    $[40] = onSelectOperator;
    $[41] = operator.id;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== resolvedMoreInfoHref || $[44] !== showMoreInfo) {
    t22 = showMoreInfo && <Button variant="outline" asChild={true} size="lg"><Link href={resolvedMoreInfoHref}><span>More Info</span><Info className="size-4" /></Link></Button>;
    $[43] = resolvedMoreInfoHref;
    $[44] = showMoreInfo;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== t10 || $[47] !== t20 || $[48] !== t21 || $[49] !== t22) {
    t23 = <Card className="gap-4 border-none p-3 shadow-lg">{t10}{t20}{t21}{t22}</Card>;
    $[46] = t10;
    $[47] = t20;
    $[48] = t21;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  return t23;
}
export { OperatorCard };